import {VisualWorkspaceModel} from '../../../../../models/api/visualWorkspaceModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayActionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../../services/tracking';

export function trackSaveSidebarEvent(
  initialWorkspace: VisualWorkspaceModel,
  currentWorkspace: VisualWorkspaceModel,
) {
  trackTags(initialWorkspace, currentWorkspace);
  trackTeamInboxes(initialWorkspace, currentWorkspace);
  trackTeammates(initialWorkspace, currentWorkspace);
  trackRootFolders(initialWorkspace, currentWorkspace);
  trackAlphabetization(initialWorkspace, currentWorkspace);
}

function trackTeammates(initialWorkspace: VisualWorkspaceModel, currentWorkspace: VisualWorkspaceModel) {
  const initialTeammates = initialWorkspace.teammates;
  const currentTeammates = currentWorkspace.teammates;
  const currentTeammateIds = currentTeammates.map((teammate) => teammate.id);
  const initialTeammateIds = initialTeammates.map((teammate) => teammate.id);

  // Newly hidden teammates are still in currentTeammates but isSelected is false.
  const hiddenTeammates = currentTeammates.filter((teammate) => !teammate.isSelected);
  // Newly shown teammates' ids are not in initialTeammateIds but are in currentTeammateIds.
  const shownTeammates = currentTeammateIds.filter((id) => !initialTeammateIds.includes(id));

  // We have 1 update event trigger for each teammate hidden or shown.
  hiddenTeammates.forEach((teammate) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      action: WorkspaceDisplayActionsEnum.HIDE,
      section: WorkspaceDisplaySectionTrackingTypesEnum.TEAMMATES,
      if_auto_suggested: teammate.isRecentlyViewed === false,
    });
  });
  shownTeammates.forEach((teammate) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      action: WorkspaceDisplayActionsEnum.DISPLAY,
      section: WorkspaceDisplaySectionTrackingTypesEnum.TEAMMATES,
    });
  });
}

function trackTeamInboxes(initialWorkspace: VisualWorkspaceModel, currentWorkspace: VisualWorkspaceModel) {
  initialWorkspace.teams.forEach((initialTeamData) => {
    const initialTeamInboxIds = initialTeamData.inboxes.map(({id}) => id);
    const initialTeamViewIds = initialTeamData.customViews.map(({id}) => id);
    const currentVisualWorkspaceTeamData = currentWorkspace.teams.find(
      (currentTeamData) => currentTeamData.teamId === initialTeamData.teamId,
    );
    if (currentVisualWorkspaceTeamData) {
      const currentTeamInboxIds = currentVisualWorkspaceTeamData.inboxes.map((inbox) => inbox.id);
      const currentTeamViewIds = currentVisualWorkspaceTeamData.customViews.map((view) => view.id);

      // Newly shown items will be in current items but not in initial items.
      const shownTeamInboxes = currentTeamInboxIds.filter((id) => !initialTeamInboxIds.includes(id));
      const shownTeamViews = currentTeamViewIds.filter((id) => !initialTeamViewIds.includes(id));
      // Newly hidden items have isSelected as false.
      const hiddenTeamInboxes = currentVisualWorkspaceTeamData.inboxes.filter(
        (teamInbox) => !teamInbox.isSelected,
      );
      const hiddenTeamViews = currentVisualWorkspaceTeamData.customViews.filter(
        (teamView) => !teamView.isSelected,
      );

      // We have 1 update event trigger for each team inbox hidden or shown.
      hiddenTeamInboxes.forEach((inbox) => {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
          action: WorkspaceDisplayActionsEnum.HIDE,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
          if_auto_suggested: inbox.isRecentlyViewed === false,
        });
      });
      shownTeamInboxes.forEach((inbox) => {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
          action: WorkspaceDisplayActionsEnum.DISPLAY,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
        });
      });
      // We have 1 update event trigger for each team view hidden or shown.
      hiddenTeamViews.forEach((view) => {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
          action: WorkspaceDisplayActionsEnum.HIDE,
          section: WorkspaceDisplaySectionTrackingTypesEnum.CUSTOM_VIEW,
          if_auto_suggested: view.isRecentlyViewed === false,
        });
      });
      shownTeamViews.forEach((inbox) => {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
          action: WorkspaceDisplayActionsEnum.DISPLAY,
          section: WorkspaceDisplaySectionTrackingTypesEnum.CUSTOM_VIEW,
        });
      });
    }
  });
}

function trackTags(initialWorkspace: VisualWorkspaceModel, currentWorkspace: VisualWorkspaceModel) {
  const initialTagIds = initialWorkspace.teammateWorkspaceTags.map((tag) => tag.id);
  const currentTagIds = currentWorkspace.teammateWorkspaceTags.map((tag) => tag.id);
  // Newly hidden tags will have isSelected as false.
  const hiddenTags = currentWorkspace.teammateWorkspaceTags.filter((tag) => !tag.isSelected);
  // Newly shown tags' ids will be in currentTagIds but not in initialTagIds.
  const shownTags = currentTagIds.filter((id) => !initialTagIds.includes(id));
  // We have 1 update event trigger for each tag hidden or shown.
  hiddenTags.forEach((tag) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      action: WorkspaceDisplayActionsEnum.HIDE,
      section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
      if_auto_suggested: tag.isRecentlyViewed === false,
    });
  });
  shownTags.forEach((tag) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      action: WorkspaceDisplayActionsEnum.DISPLAY,
      section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
    });
  });
}

function trackRootFolders(initialWorkspace: VisualWorkspaceModel, currentWorkspace: VisualWorkspaceModel) {
  const hiddenLabels = currentWorkspace.rootFolders.filter((folder) => {
    const initialLabel = initialWorkspace.rootFolders.find(({id}) => id === folder.id);
    const previouslySelected = initialLabel?.isSelected;
    const currentlySelected = folder.isSelected;

    return previouslySelected && !currentlySelected;
  });
  hiddenLabels.forEach((folder) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      action: WorkspaceDisplayActionsEnum.HIDE,
      section: folder.id,
    });
  });

  const shownLabels = currentWorkspace.rootFolders.filter((folder) => {
    const initialLabel = initialWorkspace.rootFolders.find(({id}) => id === folder.id);
    const previouslySelected = initialLabel?.isSelected;
    const currentlySelected = folder.isSelected;

    return !previouslySelected && currentlySelected;
  });
  shownLabels.forEach((label) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      action: WorkspaceDisplayActionsEnum.DISPLAY,
      section: label.id,
    });
  });
}

function trackAlphabetization(
  initialWorkspace: VisualWorkspaceModel,
  currentWorkspace: VisualWorkspaceModel,
) {
  // track change in isAlphabetized
  if (!currentWorkspace.isAlphabetized && initialWorkspace.isAlphabetized) {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      has_alphabetization_changed: true,
    });
  } else {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_UPDATE, {
      has_alphabetization_changed: false,
    });
  }
}
